// @flow

import type { GenericEventConfig, ProductItem } from '@chatchamp/chatchamp-types'

import { type EventPayload } from '../../../common/iframeMessageTypes'

import { EVENT_SENDERS, type EventSenderType } from '../types'
import { fireGoogleAnalyticsEvent, isGaAvailable, isGaDataLayerAvailable, pushEventToDataLayer } from './gaUtils'
import { getPayloadExtraProperties } from './getPayloadExtraProperties'

type ProductItemGA = {
  item_id: string,
  item_brand: ?string,
  item_category: ?string,
  price: ?number,
  quantity: number
}

// only exported for unit test
export function getGaEventConfig (eventName: string, eventSender: EventSenderType = EVENT_SENDERS.CHATBOT): GenericEventConfig {
  if (eventSender === EVENT_SENDERS.WIZARD) {
    return window.chatchamp.config.googleAnalytics?.wizardEvents?.find((e) => e.eventName === eventName)
  } else {
    return window.chatchamp.config.googleAnalytics?.events.find((e) => e.eventName === eventName)
  }
}

function formatGaEventConfig (payload: EventPayload): GenericEventConfig | void {
  const gaEventConfig: GenericEventConfig = getGaEventConfig(payload.event, payload.eventSender)

  if (!gaEventConfig) return

  if (!gaEventConfig.payload) gaEventConfig.payload = {}
  gaEventConfig.payload.non_interaction = (payload.event === 'loaded')
  // $FlowFixMe
  gaEventConfig.payload = {
    ...gaEventConfig.payload,
    ...getPayloadExtraProperties(payload),
    // Thiago; 06.09.2023; this fallback is necessary for maintaining backward-compatibility when the payload lacks a defined eventSender.
    advisorType: payload.eventSender ?? EVENT_SENDERS.CHATBOT
  }

  const products = payload.product ? [payload.product] : payload.products
  if (products) gaEventConfig.payload = replaceProductsPlaceholder(gaEventConfig.payload, products)

  return gaEventConfig
}

export function forwardEventToGa (payload: EventPayload): void {
  const { config } = window.chatchamp

  const gaEventConfig = formatGaEventConfig(payload)
  if (!gaEventConfig) return

  if (config.googleAnalytics?.sendOption === 'DATA_LAYER' && isGaDataLayerAvailable()) {
    pushEventToDataLayer(gaEventConfig)
    return
  }

  if (!config.googleAnalyticsId) return
  if (!isGaAvailable()) return

  fireGoogleAnalyticsEvent(gaEventConfig, config.googleAnalyticsId)
}

function formatProductItemToGA (product: ProductItem): ProductItemGA {
  return {
    item_id: product.productIdentifier,
    item_brand: product.brand ? product.brand.toString() : null,
    item_category: product.category ?? null,
    price: (product.salePriceNumber || product.priceNumber)
      ? Number(product.salePriceNumber || product.priceNumber)
      : null,
    quantity: 1
  }
}

export function replaceProductsPlaceholder (payload: Object, products: Array<ProductItem>): Object {
  // $FlowFixMe
  return JSON.parse(JSON.stringify(payload).replaceAll('"{{items}}"', JSON.stringify(products.map(formatProductItemToGA))))
}
